import axios from "axios";
import { Chart, PivotGrid } from "devextreme-react";
import {
  CommonSeriesSettings,
  Legend,
  Size,
  Tooltip,
  ValueAxis,
} from "devextreme-react/chart";
import { Scrolling } from "devextreme-react/pivot-grid";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const PivotGridAndChartWrapper = styled.div`
  h3 {
    margin: 0;
    margin-bottom: 4px;
  }
  .chartWrapper {
    margin-top: 16px;
    > div {
      border: 1px solid "#e6e6e6";
      padding: 4px;
      svg {
        margin: 0 auto;
      }
    }
  }
`;

const TooltipWrapper = styled.div`
  h3 {
    margin: 0;
    margin-bottom: 8px;
    border-bottom: 1px solid gray;
    font-size: 1.4em;
  }
  h3 + div {
    display: grid;
    grid-template-columns: 60px auto;
    grid-template-rows: repeat(auto-fill, auto);
    color: #e6e6e6;
    font-size: 1.2em;
    line-height: 150%;
    > div:nth-child(2n) {
      text-align: right;
    }
  }
`;

const IssueCountPivotGrid = ({
  contrNo,
  isVisible,
  showChart,
  target,
  targetName,
  title,
  yearMonth,
}) => {
  const [colors, setColors] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  const chartRef = useRef(null);
  const maxColumnGrandTotalRef = useRef(0);
  const maxRowGrandTotalRef = useRef(0);
  const pivotGridRef = useRef(null);

  useEffect(() => {
    if (isVisible) {
      axios
        .get(
          `${
            process.env.REACT_APP_BECCO_API_URL
          }/api/mthlyReport/proj/iss/sum/${yearMonth}?${
            contrNo ? `contrNo=${contrNo}&` : ""
          }target=${target}&targetName=${targetName}`
        )
        .then((res) => {
          setPivotData(
            new PivotGridDataSource({
              fields: [
                {
                  caption: "Grade",
                  dataField: "gradName",
                  area: "row",
                  sortOrder: "desc",
                  width: 80,
                },
                {
                  dataField: `${target}Name`,
                  area: "column",
                  sortingMethod: (e) => e.index,
                  sortOrder: "asc",
                },
                {
                  caption: "Count",
                  dataField: "count",
                  dataType: "number",
                  summaryType: "sum",
                  area: "data",
                  calculateSummaryValue: handleCalculateMax,
                },
              ],
              store: res.data,
            })
          );
          setColors(
            res.data.reduce(
              (res, ele) => ({ ...res, [ele.gradName]: ele.color }),
              {}
            )
          );
        });
    }
  }, [isVisible]);

  const handleBindChart = (e) => {
    if (showChart && chartRef.current) {
      e.component.bindChart(chartRef.current.instance, {
        alternateDataFields: false,
        customizeSeries: (seriesName) => ({
          color: colors[seriesName],
        }),
        dataFieldsDisplayMode: "splitPanes",
      });
    }
  };

  const handleCalculateMax = (e) => {
    const isGrandColumnTotalCell = !e.parent("column");
    const isGrandRowTotalCell = !e.parent("row");
    const targetRef = isGrandRowTotalCell
      ? maxRowGrandTotalRef
      : maxColumnGrandTotalRef;
    const nextCell = isGrandRowTotalCell ? e.next("column") : e.next("row");

    if (isGrandRowTotalCell || isGrandColumnTotalCell) {
      if (nextCell) {
        targetRef.current = Math.max(
          targetRef.current,
          e.value(),
          nextCell.value()
        );
      }
    }

    return e.value();
  };

  const handleOnCellPrepared = (e) => {
    // * 전체 cell style.
    e.cellElement.style.backgroundColor = "white";
    e.cellElement.style.padding = "5px 0px";
    e.cellElement.style.width = "100px";
    e.cellElement.style.textAlign = "center";
    // * column Field 부분: "bold", 배경색 연초록
    if (e.area === "column" && e.rowIndex === 0) {
      e.cellElement.style.fontWeight = "bold";
      e.cellElement.style.backgroundColor = "#E2EFDA";
    }
    // * row Field 부분: "bold", "issueGrade 배경"
    if (e.area === "row" && e.columnIndex === 0) {
      e.cellElement.style.fontWeight = "bold";
      e.cellElement.style.backgroundColor = "#E2EFDA";
      if (e.cell.path) {
        e.cellElement.style.backgroundColor = colors?.[e.cell.text];
      }
    }
    // * data 부분: value가 0이면, textContent = "" (total부분은 표시)
    if (
      e.area === "data" &&
      e.cell.value === 0 &&
      e.cell.rowType === "D" &&
      e.cell.columnType === "D"
    ) {
      e.cellElement.textContent = "";
    }
    // * 최댓값 summary value는 분홍색으로 강조 표시. (최댓값이 0이 아닌 경우)
    if (
      (e.cell.rowType === "GT" &&
        e.cell.columnType !== "GT" &&
        maxRowGrandTotalRef.current !== 0 &&
        e.cell.value === maxRowGrandTotalRef.current) ||
      (e.cell.rowType !== "GT" &&
        e.cell.columnType === "GT" &&
        maxColumnGrandTotalRef.current !== 0 &&
        e.cell.value === maxColumnGrandTotalRef.current)
    ) {
      e.cellElement.style.color = "red";
      e.cellElement.style.backgroundColor = "pink";
    }
  };

  const renderTooltip = (e) => {
    const { argumentText, seriesName: grade, value } = e;
    return (
      <TooltipWrapper>
        <h3>{argumentText}</h3>
        <div>
          <div>Grade:</div>
          <div>{grade}</div>
          <div>Count:</div>
          <div>{value}</div>
        </div>
      </TooltipWrapper>
    );
  };

  return isVisible && colors && pivotData ? (
    <PivotGridAndChartWrapper showChart={showChart}>
      {title && <h3>{title}</h3>}
      <PivotGrid
        dataSource={pivotData}
        fieldChooser={{ enabled: false }}
        onCellPrepared={handleOnCellPrepared}
        onContentReady={handleBindChart}
        ref={pivotGridRef}
        showBorders={true}
        texts={{ grandTotal: "Total" }}
        width={"100%"}
      >
        <Scrolling mode="virtual" showScrollbar={"always"} />
      </PivotGrid>
      <div
        className="chartWrapper"
        style={{ display: `${showChart ? "block" : "none"}` }}
      >
        <Chart
          margin={{ top: 16 }}
          ref={chartRef}
          rotated={true}
          title={{
            font: { size: "1.2em" },
            horizontalAlignment: "center",
            text: title,
          }}
          style={{
            border: "1px solid #e6e6e6",
            padding: "4px",
            margin: "0 auto",
          }}
        >
          <CommonSeriesSettings type="bar" visible={showChart} />
          <ValueAxis name="count" tickInterval={1} />
          <Legend
            horizontalAlignment="center"
            orientation="horizontal"
            verticalAlignment="bottom"
          />
          <Size height={350} width={550} />
          <Tooltip
            enabled={true}
            location="center"
            shared={true}
            contentRender={renderTooltip}
            zIndex={10000}
          />
        </Chart>
      </div>
    </PivotGridAndChartWrapper>
  ) : (
    ""
  );
};
export default IssueCountPivotGrid;
